import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';

const DataContext = createContext(null);

// Cache TTL for the products/customers snapshots. 12h: long enough to avoid
// repeatedly re-pulling the large customers collection, and safe because every
// create/edit patches the cache directly (addCustomerToCache / updateCustomerInCache
// / updateProduct), so new/edited records are visible immediately regardless of TTL.
const CACHE_TTL_MS = 12 * 60 * 60 * 1000;

// Fields the app reads from the CACHED customers array (Customers list + its edit
// form, the document customer pickers / buildDocData, the Dashboard badge, and the
// SALE/INVOICE account lookup). CustomerProfile fetches the full customer doc itself
// via getDoc, so profile-only fields (createdAt, accountOpenedDate, …) are safely
// omitted from the persisted copy to reduce its size vs the localStorage quota.
// `phones` is the multi-phone array [{number,label,isPrimary}]; `phone` is the
// legacy single string kept in sync with the primary. BOTH must be cached: the
// array feeds the editor/search, and dropping it here would let a save reach
// Firestore but vanish from the cache — appearing to work until the next reload.
const CUSTOMER_CACHE_FIELDS = [
  'id', 'name', 'phone', 'phones', 'email', 'address', 'customerNumber',
  'isAccountCustomer', 'taxExempt', 'notes',
  'creditLimit', 'paymentTerms', 'currentBalance',
  'defaultPriceLevel',
  // Credit hold. The warning is shown from the CACHED customer in the customer
  // list, the document customer pickers and the document screens, so dropping
  // these would hide a hold everywhere except the profile — which fetches its
  // own full doc — and a salesperson would never see it.
  'creditHold', 'creditHoldReason', 'creditHoldBy', 'creditHoldAt',
];
const slimCustomer = (c) => {
  const out = {};
  CUSTOMER_CACHE_FIELDS.forEach(f => { if (c[f] !== undefined) out[f] = c[f]; });
  return out;
};

function loadFromStorage(key) {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;
    const { data, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp < CACHE_TTL_MS) return data;
    return null;
  } catch {
    return null;
  }
}

function getStorageTimestamp(key) {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;
    return JSON.parse(cached).timestamp || null;
  } catch {
    return null;
  }
}

function saveToStorage(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
  } catch (e) {
    // Surface (don't swallow): if a cache is too big for the ~5MB localStorage
    // quota it silently fails to persist and re-fetches on every mount — this
    // warning makes that diagnosable instead of invisible.
    console.warn(`[DataContext] Could not persist "${key}" to localStorage (quota?):`, e?.message || e);
  }
}

// Persist customers as a SLIM projection (profile-only fields dropped) so the large
// customer list is less likely to blow the localStorage quota. In-memory state keeps
// the full objects; the slim set still carries every field the cached array is read
// for outside CustomerProfile (which re-fetches its own full doc).
function saveCustomersToStorage(list) {
  saveToStorage('atlantic_customers', list.map(slimCustomer));
}

export function DataProvider({ children }) {
  const [products, setProducts] = useState(() => loadFromStorage('atlantic_products') || []);
  const [customers, setCustomers] = useState(() => loadFromStorage('atlantic_customers') || []);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [productsLoadedAt, setProductsLoadedAt] = useState(() => getStorageTimestamp('atlantic_products'));
  const [customersLoadedAt, setCustomersLoadedAt] = useState(() => getStorageTimestamp('atlantic_customers'));

  const fetchingProducts = useRef(false);
  const fetchingCustomers = useRef(false);

  const fetchProducts = useCallback(async (force = false) => {
    if (fetchingProducts.current) return;
    if (!force) {
      const cached = loadFromStorage('atlantic_products');
      if (cached && cached.length > 0) {
        setProducts(cached);
        return;
      }
    }
    fetchingProducts.current = true;
    setLoadingProducts(true);
    try {
      const q = query(collection(db, 'products'), orderBy('category'));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      const ts = Date.now();
      setProducts(data);
      setProductsLoadedAt(ts);
      saveToStorage('atlantic_products', data);
    } catch (err) {
      console.error('Error loading products:', err);
    }
    setLoadingProducts(false);
    fetchingProducts.current = false;
  }, []);

  const fetchCustomers = useCallback(async (force = false) => {
    if (fetchingCustomers.current) return;
    if (!force) {
      const cached = loadFromStorage('atlantic_customers');
      if (cached && cached.length > 0) {
        setCustomers(cached);
        return;
      }
    }
    fetchingCustomers.current = true;
    setLoadingCustomers(true);
    try {
      const q = query(collection(db, 'customers'), orderBy('customerNumber', 'desc'));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      const ts = Date.now();
      setCustomers(data);
      setCustomersLoadedAt(ts);
      saveCustomersToStorage(data);
    } catch (err) {
      console.error('Error loading customers:', err);
    }
    setLoadingCustomers(false);
    fetchingCustomers.current = false;
  }, []);

  const refreshProducts = useCallback(() => {
    localStorage.removeItem('atlantic_products');
    return fetchProducts(true);
  }, [fetchProducts]);

  const refreshCustomers = useCallback(() => {
    localStorage.removeItem('atlantic_customers');
    return fetchCustomers(true);
  }, [fetchCustomers]);

  const updateProduct = useCallback((id, fields) => {
    setProducts(prev => {
      if (!prev.length) return prev;
      const updated = prev.map(p => p.id === id ? { ...p, ...fields } : p);
      saveToStorage('atlantic_products', updated);
      return updated;
    });
  }, []);

  // Add a newly-created customer to the in-memory + localStorage cache so it
  // shows up everywhere immediately without a full Firestore re-fetch.
  const addCustomerToCache = useCallback((customer) => {
    setCustomers(prev => {
      if (prev.some(c => c.id === customer.id)) return prev;
      const updated = [customer, ...prev];
      saveCustomersToStorage(updated);
      return updated;
    });
  }, []);

  // Patch an existing customer in the in-memory + persisted cache so edits (name,
  // convert-to-account, credit limit, balance, …) are reflected instantly in the
  // Customers list, pickers, and the SALE/INVOICE lookup — without a full re-fetch
  // and regardless of the cache TTL. Mirrors updateProduct.
  const updateCustomerInCache = useCallback((id, fields) => {
    setCustomers(prev => {
      if (!prev.length) return prev;
      const updated = prev.map(c => c.id === id ? { ...c, ...fields } : c);
      saveCustomersToStorage(updated);
      return updated;
    });
  }, []);

  // Drop a deleted customer from the in-memory + persisted cache so it vanishes
  // from the Customers list, pickers, and lookups instantly — no full re-fetch.
  // Mirrors addCustomerToCache / updateCustomerInCache.
  const removeCustomerFromCache = useCallback((id) => {
    setCustomers(prev => {
      if (!prev.length) return prev;
      const updated = prev.filter(c => c.id !== id);
      saveCustomersToStorage(updated);
      return updated;
    });
  }, []);

  useEffect(() => {
    fetchProducts();
    fetchCustomers();
  }, [fetchProducts, fetchCustomers]);

  return (
    <DataContext.Provider value={{
      products,
      customers,
      loadingProducts,
      loadingCustomers,
      refreshProducts,
      refreshCustomers,
      updateProduct,
      addCustomerToCache,
      updateCustomerInCache,
      removeCustomerFromCache,
      productsLoadedAt,
      customersLoadedAt,
    }}>
      {children}
    </DataContext.Provider>
  );
}

export function useData() {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error('useData must be used inside DataProvider');
  return ctx;
}
